import { motion } from 'motion/react';
import { ArrowUpRight } from 'lucide-react';

export type EventsHighlightItem = {
	meta: string;
	title: string;
	description: string;
	url?: string;
};

type EventsHighlightProps = {
	eyebrow?: string;
	title?: string;
	description?: string;
	events?: EventsHighlightItem[];
};

export function EventsHighlight({
	eyebrow,
	title,
	description,
	events,
}: EventsHighlightProps) {
	const defaultEvents: EventsHighlightItem[] = [
		{
			meta: 'This week',
			title: 'Sahaj Path & morning divan',
			description: 'Regular prayer gatherings and daily temple rhythms.',
		},
		{
			meta: 'Upcoming',
			title: 'Youth learning circle',
			description: 'Workshops, Punjabi classes, or youth discussions.',
		},
		{
			meta: 'Community',
			title: 'Langar seva gathering',
			description: 'Open seva — all welcome to contribute and participate.',
		},
	];

	const items = events && events.length > 0 ? events : defaultEvents;

	return (
		<section className="relative overflow-hidden border-b border-border bg-card">
			{/* Atmospheric right-side glow */}
			<div
				aria-hidden="true"
				className="pointer-events-none absolute right-0 top-1/2 h-[500px] w-[500px] -translate-y-1/2 translate-x-1/3 rounded-full bg-accent/[0.05] blur-[130px]"
			/>

			<div className="relative mx-auto max-w-6xl px-6 py-24 md:px-12 lg:px-16">
				<div className="grid gap-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.45fr)] lg:gap-20">

					{/* ── Left: sticky header ─────────────────────── */}
					<motion.div
						initial={{ opacity: 0, y: 18 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.06 }}
						transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
						className="lg:sticky lg:top-24 lg:self-start"
					>
						{/* Eyebrow */}
						<div className="flex items-center gap-3">
							<div className="h-px w-6 shrink-0 bg-accent/60" aria-hidden="true" />
							<p className="text-[10px] font-semibold uppercase tracking-[0.44em] text-accent/75">
								{eyebrow || 'Gatherings & moments'}
							</p>
						</div>

						<h2 className="mt-6 font-serif text-4xl leading-[1.02] tracking-tight text-foreground md:text-5xl">
							{title || 'Upcoming events at the Gurdwara.'}
						</h2>

						<div className="mt-6 flex items-start gap-4">
							<div className="mt-2 h-px w-8 shrink-0 bg-foreground/15" aria-hidden="true" />
							<p className="text-[13.5px] leading-[1.9] text-foreground/42">
								{description || 'Festivals, prayer services, educational sessions, and visitor notices — calm and structured.'}
							</p>
						</div>

						{/* Event count pill */}
						<div className="mt-9 inline-flex items-center gap-2 rounded-full border border-border/80 bg-background/60 px-4 py-1.5">
							<span className="h-1.5 w-1.5 rounded-full bg-accent shadow-[0_0_5px_rgba(184,154,50,0.7)]" aria-hidden="true" />
							<span className="text-[11px] font-semibold uppercase tracking-[0.36em] text-foreground/50">
								{items.length} event{items.length !== 1 ? 'i' : 'o'}
							</span>
						</div>
					</motion.div>

					{/* ── Right: event list ───────────────────────── */}
					<div>
						{items.map((event, i) => {
							const isLast = i === items.length - 1;

							const inner = (
								<motion.div
									initial={{ opacity: 0, y: 20 }}
									whileInView={{ opacity: 1, y: 0 }}
										viewport={{ once: true, amount: 0.06 }}
									transition={{ duration: 0.55, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
									className="group relative flex items-start gap-7 py-9"
								>
									{/* Index rail */}
									<div className="flex flex-col items-center gap-2 pt-0.5">
										<span className="font-serif text-[11px] italic text-foreground/22">
											0{i + 1}
										</span>
										{!isLast && (
											<div className="mt-1 w-px flex-1 bg-gradient-to-b from-border/70 to-transparent" style={{ minHeight: '2.5rem' }} aria-hidden="true" />
										)}
									</div>

									{/* Content */}
									<div className="flex min-w-0 flex-1 items-start justify-between gap-6">
										<div className="min-w-0 flex-1">
											{/* Meta tag */}
											<div className="flex items-center gap-2">
												<div className="h-1 w-1 rounded-full bg-accent/80" aria-hidden="true" />
												<p className="text-[10px] font-semibold uppercase tracking-[0.44em] text-accent/70">
													{event.meta}
												</p>
											</div>

											{/* Title */}
											<p className="mt-3 font-serif text-[1.35rem] leading-[1.18] tracking-tight text-foreground transition-colors duration-200 group-hover:text-accent">
												{event.title}
											</p>

											{/* Animated underrule */}
											<div
												aria-hidden="true"
												className="mt-3.5 h-px w-6 bg-foreground/15 transition-all duration-500 group-hover:w-10 group-hover:bg-accent/55"
											/>

											{/* Description */}
											<p className="mt-3 text-[13px] leading-[1.85] text-foreground/40">
												{event.description}
											</p>
										</div>

										{/* Arrow — only shown if has url */}
										{event.url && (
											<div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/70 text-foreground/20 transition-all duration-200 group-hover:border-accent/50 group-hover:text-accent">
												<ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
											</div>
										)}
									</div>
								</motion.div>
							);

							const divider = !isLast ? (
								<div className="h-px bg-gradient-to-r from-border/60 via-border/30 to-transparent" aria-hidden="true" />
							) : null;

							if (event.url) {
								return (
									<div key={`${event.title}-${i}`}>
										<a href={event.url} className="block focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent">
											{inner}
										</a>
										{divider}
									</div>
								);
							}

							return (
								<div key={`${event.title}-${i}`}>
									{inner}
									{divider}
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</section>
	);
}
